/**
 * Type definitions for CSV interception layer
 */

import type { DataSource } from '../types/api';

/**
 * Interception source type
 */
export type InterceptionSource = 'fetch' | 'xhr' | 'blob' | 'download';

/**
 * Intercepted CSV data
 */
export interface InterceptedCSV {
  /** Unique ID for this interception */
  id: string;
  /** CSV data as string */
  data: string;
  /** Data size in bytes */
  size: number;
  /** Source information */
  source: DataSource;
  /** Interception source type */
  interceptedVia: InterceptionSource;
  /** Timestamp when intercepted */
  timestamp: number;
  /** Hash of the data (for deduplication) */
  hash: string;
  /** Suggested table name (if available) */
  suggestedName?: string;
}

/**
 * Interception handler callback
 */
export type InterceptionHandler = (csv: InterceptedCSV) => void | Promise<void>;

/**
 * MIME types that indicate CSV data
 */
export const CSV_MIME_TYPES = [
  'text/csv',
  'application/csv',
  'text/comma-separated-values',
  'application/vnd.ms-excel',
  'text/plain', // Sometimes CSV is served as text/plain
] as const;

/**
 * Check if a MIME type indicates CSV data
 */
export function isCSVMimeType(mimeType: string | null | undefined): boolean {
  if (!mimeType) return false;
  const normalized = mimeType.toLowerCase().split(';')[0].trim();
  return CSV_MIME_TYPES.some(type => normalized.includes(type));
}

/**
 * Check if a filename suggests CSV data
 */
export function isCSVFilename(filename: string | null | undefined): boolean {
  if (!filename) return false;
  return filename.toLowerCase().endsWith('.csv');
}

/**
 * Check if data looks like CSV (heuristic check)
 */
export function looksLikeCSV(data: string, maxLines: number = 10): boolean {
  if (!data || data.length === 0) return false;

  // Get first few lines
  const lines = data.split('\n').slice(0, maxLines).filter(line => line.trim());
  if (lines.length < 2) return false; // Need at least header + 1 data row

  // Check if lines have consistent comma counts
  const commaCounts = lines.map(line => (line.match(/,/g) || []).length);
  const firstCount = commaCounts[0];

  // At least 1 comma (2 columns) and consistent across rows
  if (firstCount === 0) return false;

  // Allow some variation (quoted fields might affect count)
  const consistent = commaCounts.every(count => Math.abs(count - firstCount) <= 2);

  return consistent;
}

/**
 * Generate a hash for CSV data (for deduplication)
 */
export function hashCSV(data: string): string {
  // Simple hash function (DJB2)
  let hash = 5381;
  for (let i = 0; i < data.length; i++) {
    hash = ((hash << 5) + hash) + data.charCodeAt(i);
  }
  return hash.toString(36);
}

/**
 * Extract filename from URL or Content-Disposition header
 */
export function extractFilename(
  url: string | null | undefined,
  contentDisposition: string | null | undefined
): string | undefined {
  // Try Content-Disposition header first
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].replace(/['"]/g, '');
    }
  }

  // Try URL
  if (url) {
    try {
      const urlObj = new URL(url, window.location.href);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop();
      if (filename && filename.includes('.')) {
        return filename;
      }
    } catch (e) {
      // Invalid URL
    }
  }

  return undefined;
}

/**
 * Suggest a table name from filename
 */
export function suggestTableName(filename: string | undefined): string | undefined {
  if (!filename) return undefined;

  // Remove extension
  let name = filename.replace(/\.csv$/i, '');

  // Remove common suffixes
  name = name.replace(/[-_](export|download|data|report)$/i, '');

  // Replace non-alphanumeric with underscores
  name = name.replace(/[^a-zA-Z0-9_]/g, '_');

  // Remove leading/trailing underscores
  name = name.replace(/^_+|_+$/g, '');

  // Ensure it doesn't start with a number
  if (/^\d/.test(name)) {
    name = 'table_' + name;
  }

  // Convert to lowercase
  name = name.toLowerCase();

  return name || undefined;
}

/**
 * Check if a URL matches any of the given patterns
 */
export function matchesUrlPattern(url: string, patterns: string[]): boolean {
  if (patterns.includes('*')) return true;

  return patterns.some(pattern => {
    // Convert glob pattern to regex
    const regex = new RegExp(
      '^' + pattern.replace(/\*/g, '.*').replace(/\?/g, '.') + '$'
    );
    return regex.test(url);
  });
}

/**
 * Interceptor interface
 */
export interface Interceptor {
  /** Start intercepting */
  start(): void;
  /** Stop intercepting and restore original functions */
  stop(): void;
  /** Check if currently intercepting */
  isActive(): boolean;
}
